import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import R from "ramda";
import classNames from "classnames";
import { Tabs } from "@simpleryo/syw-uikit";
import getDisplayName from "../utils/getDisplayName";
import * as actionCreators from "../actions";

const calculateTabState = ({ groupsList, groups, fields }) => {
  const panes = R.filter(groupName => {
    if (groups[groupName].status === "active") {
      return true;
    }
    return R.find(name => fields[name].value)(groups[groupName].fieldsList);
  }, groupsList);

  const activeKey = R.last(panes);
  const isLastTab = panes.length === groupsList.length;
  return {
    panes,
    activeKey,
    isLastTab
  };
};

const connectEditableTabsGroups = WrappedComponent => {
  class ConnectEditableTabsGroups extends PureComponent {
    constructor(props) {
      super(props);
      const { panes = [], activeKey = "", isLastTab } = calculateTabState(
        props
      );
      this.state = {
        panes,
        activeKey,
        isLastTab
      };
    }

    getSnapshotBeforeUpdate(prevProps, prevState) {
      const { panes, activeKey, isLastTab } = calculateTabState(this.props);
      if (R.equals(panes, prevState.panes)) {
        return null;
      }

      return {
        panes,
        activeKey,
        isLastTab
      };
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
      if (snapshot !== null) {
        this.setState(snapshot);
      }
    }

    componentDidMount() {
      const { panes, isLastTab } = this.state;
      const { groupsList, dispatchTriggerFieldActions } = this.props;
      if (R.not(R.isEmpty(panes))) {
        // Update the Groups Status
        let payload = { activeGroups: panes };
        if (R.not(isLastTab)) {
          payload = {
            ...payload,
            inactiveGroups: R.difference(groupsList, panes)
          };
        }
        dispatchTriggerFieldActions(payload);
      }
    }

    onTabChange = activeKey => this.setState({ activeKey });

    onTabEdit = (targetKey, action) => this[action](targetKey);

    add = () => {
      if (this.state.isLastTab) {
        return;
      }

      const { groupsList } = this.props;
      const inactiveGroups = R.difference(groupsList, this.state.panes);
      const isLastTab = inactiveGroups.length === 1;
      const activeKey = R.head(inactiveGroups);
      const panes = [...this.state.panes, activeKey];
      this.setState({ isLastTab, activeKey, panes });

      // Active the Group
      this.props.dispatchTriggerFieldActions({
        activeGroups: [activeKey]
      });
    };

    remove = targetKey => {
      const panes = R.filter(pane => pane !== targetKey, this.state.panes);
      const activeKey = R.last(panes);
      const isLastTab = false;
      this.setState({ panes, activeKey, isLastTab });

      // Inactive the Group
      this.props.dispatchTriggerFieldActions({
        inactiveGroups: [targetKey]
      });
    };

    render() {
      const { className, groups } = this.props;
      const { isLastTab, activeKey, panes } = this.state;
      if (panes.length === 0) {
        return null;
      }
      return (
        <Tabs
          className={classNames("light-grey", className)}
          type="editable-card"
          activeKey={activeKey}
          onChange={this.onTabChange}
          onEdit={this.onTabEdit}
          hideAdd
          tabBarExtraContent={
            <a
              className={classNames("link", { disabled: isLastTab })}
              onClick={this.add}
            >
              + Add
            </a>
          }
        >
          {R.addIndex(R.map)((groupName, index) => {
            const group = groups[groupName];
            return (
              <Tabs.TabPane
                key={group.name}
                tab={`${group.title}${index + 1}`}
                closable={index !== 0}
              >
                <WrappedComponent
                  injectedProps={{
                    group
                  }}
                  {...this.props}
                />
              </Tabs.TabPane>
            );
          }, panes)}
        </Tabs>
      );
    }
  }

  ConnectEditableTabsGroups.displayName = `ConnectEditableTabsGroups(${getDisplayName(
    WrappedComponent
  )})`;

  ConnectEditableTabsGroups.propTypes = {
    className: PropTypes.string,
    groupsList: PropTypes.array.isRequired,
    groups: PropTypes.object.isRequired,
    fields: PropTypes.object.isRequired,
    dispatchTriggerFieldActions: PropTypes.func.isRequired
  };

  const mapStateToProps = ({ application }) => ({
    groups: application.groups,
    fields: application.fields
  });

  const mapDispatchToProps = {
    dispatchTriggerFieldActions: actionCreators.triggerFieldActions
  };

  return connect(mapStateToProps, mapDispatchToProps)(
    ConnectEditableTabsGroups
  );
};

export default connectEditableTabsGroups;
